import { EXPO_CONFIG } from '@/api/expoConfig';
import { fetchDedaloApi } from '@/api/dedaloApi';
import {
    MymLinksSchema,
    NavigationRootSchema,
    NavigationChildrenSchema,
    NavigationPageSchema,
    ReportSchema,
} from '@/schemas/navigationSchema';
import { parseJsonArray } from '@/utils/jsonUtils';

const { table, rootId, chaptersParentId, specialPages } = EXPO_CONFIG;

function fetchNavigationRoot(lang) {
    return fetchDedaloApi(
        { table, section_id: rootId, ar_fields: ['title'], order: 'norder' },
        NavigationRootSchema,
        lang
    );
}

function fetchChaptersParent(lang) {
    return fetchDedaloApi(
        { table, section_id: chaptersParentId, ar_fields: ['children'], order: 'norder' },
        NavigationChildrenSchema,
        lang
    );
}

function fetchGroups(firstLevelIds, lang) {
    return fetchDedaloApi(
        {
            table,
            section_id: firstLevelIds.join(','),
            ar_fields: ['title', 'children'],
            order: 'norder',
        },
        NavigationRootSchema,
        lang
    );
}

function fetchChapterPages(chapterIds, lang) {
    return fetchDedaloApi(
        {
            table,
            section_id: chapterIds.join(','),
            ar_fields: ['term', 'web_path', 'menu', 'norder'],
            order: 'norder',
        },
        NavigationPageSchema,
        lang
    );
}

function fetchSpecialPage(sectionId, lang) {
    return fetchDedaloApi(
        { table, section_id: sectionId, ar_fields: ['term', 'web_path', 'menu'] },
        NavigationPageSchema,
        lang
    );
}

function fetchReport(lang) {
    return fetchDedaloApi(
        {
            table,
            section_id: specialPages.report,
            ar_fields: ['title', 'abstract', 'pdf_resolved'],
        },
        ReportSchema,
        lang
    );
}

function fetchMymLinks(lang) {
    return fetchDedaloApi({ table, section_id: rootId, ar_fields: ['uri'] }, MymLinksSchema, lang);
}

/**
 * Fetches the exposition navigation structure from Dédalo.
 *
 * Two round trips:
 *   1. Fetch the chapters-parent term → get ordered chapter section IDs from its `children`.
 *      If chaptersLevelsDeep === 2, also fetch each group term to get its title and chapter IDs.
 *   2. Fetch all chapter terms + timeline + learnMore in parallel.
 */
export async function fetchExpoStructure(lang) {
    const { tld, chaptersLevelsDeep } = EXPO_CONFIG;

    const parseIds = (json) => JSON.parse(json).map((ref) => ref.replace(`${tld}_`, ''));

    // ── Round trip 1 ──────────────────────────────────────────────────────────
    const rootRes = await fetchNavigationRoot(lang);
    // Fetch the parent term to get the ordered list of chapter section IDs.
    const parentRes = await fetchChaptersParent(lang);

    const parentRecord = parentRes.result?.[0];
    if (!parentRecord?.children) return { pages: [], mymLinks: [], report: null };

    const expoTitle = rootRes.result?.[0]?.title || '';

    // children is a JSON string: "[\"navarra500_3\", \"navarra500_4\", ...]"
    const firstLevelIds = parseIds(parentRecord.children);

    // When chaptersLevelsDeep === 2, firstLevelIds are group terms, not pages.
    // Fetch each group to get its title and the actual chapter IDs.
    let chapterIds = firstLevelIds;
    let chaptersGroupInfo = null;
    let pageGroups = null;

    if (chaptersLevelsDeep === 2) {
        const groupsRes = await fetchGroups(firstLevelIds, lang);
        pageGroups = (groupsRes.result ?? []).map((group) => ({
            section_id: group.section_id,
            title: group.title,
        }));

        chapterIds = [];
        chaptersGroupInfo = {};
        (groupsRes.result ?? []).forEach((group, i) => {
            const ids = parseIds(group.children ?? '[]');
            for (const id of ids) {
                chaptersGroupInfo[id] = {
                    group_id: group.section_id,
                    group_title: group.title,
                    groupOrder: i,
                };
                chapterIds.push(id);
            }
        });
    }

    // ── Round trip 2 ──────────────────────────────────────────────────────────
    // Fetch all chapter terms + special pages in one parallel batch.
    // Dédalo accepts multiple section IDs as a comma-separated string.
    const [chaptersRes, timelineRes, learnMoreRes, creditsRes, reportRes, mymLinksRes] =
        await Promise.all([
            fetchChapterPages(chapterIds, lang),
            fetchSpecialPage(specialPages.timeline, lang),
            fetchSpecialPage(specialPages.learnMore, lang),
            fetchSpecialPage(specialPages.credits, lang),
            fetchReport(lang),
            fetchMymLinks(lang),
        ]);

    const toPage = (r, type) => (r ? { ...r, type } : null);

    const chapters = (chaptersRes.result ?? [])
        .map((c) => {
            const page = toPage(c, 'chapter');
            if (page && chaptersGroupInfo) {
                const groupInfo = chaptersGroupInfo[String(c.section_id)];
                page.pageGroup = groupInfo?.group_id ?? null;
                page._groupOrder = groupInfo?.groupOrder ?? 0;
            }
            return page;
        })
        .filter(Boolean);

    if (chaptersGroupInfo) {
        chapters.sort((a, b) => a._groupOrder - b._groupOrder || a.norder - b.norder);
        chapters.forEach((p) => delete p._groupOrder);
    }

    const pages = [
        ...chapters,
        toPage(timelineRes.result?.[0], 'timeline'),
        toPage(learnMoreRes.result?.[0], 'learnMore'),
        toPage(creditsRes.result?.[0], 'credits'),
    ].filter(Boolean);

    const mymLinks = parseJsonArray(mymLinksRes.result?.[0]?.uri);
    const report = reportRes.result?.[0] ?? null;

    return { expoTitle, pages, pageGroups, mymLinks, report };
}
